import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseService } from '@shared/services/base.service';
import { Observable, map, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  CashSettlementDraftErrorCode,
  CreateDraftCashSettlementRequest,
  CreateDraftCashSettlementResponse
} from '../models/cash-settlement-create.model';

@Injectable({
  providedIn: 'root'
})
export class CashSettlementService extends BaseService {
  public override headers: HttpHeaders;

  constructor(private readonly http: HttpClient) {
    super();
    this.envService = this.env.services.cashSettlements;
    this.headers = new HttpHeaders(this.envService.headers);
    this.handleError = this.handleError.bind(this);
  }

  public list(criteria): Observable<any> {
    const storeNo = this.retrieveStore().storeNo;
    const url = this.getFullUrl(this.envService.list, {
      storeNo
    });
    const params = this.getParams(criteria, true);

    return this.http
      .get<any>(url, {
        headers: this.loaderHeaders(null),
        observe: 'body',
        params
      })
      .pipe(
        map((res) => {
          if (res && res.content) {
            res.content.map((val) => {
              if (val.storeCode && val.storeName) {
                val.storeNameToDisplay = `${val.storeCode}-${val.storeName}`;
              }
            });
          }

          return res;
        })
      );
  }

  public detail(cashSettlementNo: any): Observable<any> {
    const storeNo = this.retrieveStore().storeNo;
    const url = this.getFullUrl(this.envService.detail, {
      storeNo,
      cashSettlementNo
    });

    return this.http
      .get<any>(url, {
        headers: this.loaderHeaders(500),
        observe: 'body'
      })
      .pipe(
        map((res) => {
          return res;
        })
      );
  }

  public draft(): Observable<CreateDraftCashSettlementResponse> {
    const currentDateTime = new Date();
    const storeNo = this.retrieveStore().storeNo;
    const url = this.getFullUrl(this.envService.draft, {
      storeNo
    });

    return this.http
      .post<any>(
        url,
        { currentDateTime },
        {
          headers: this.loaderHeaders(500),
          observe: 'body',
          responseType: 'json'
        }
      )
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  public submit(request: CreateDraftCashSettlementRequest): Observable<CreateDraftCashSettlementResponse> {
    const currentDateTime = new Date();
    const storeNo = this.retrieveStore().storeNo;
    const url = this.getFullUrl(this.envService.submit, {
      storeNo
    });
    request.currentDateTime = currentDateTime;

    return this.http
      .post<any>(
        url,
        { ...request },
        {
          headers: this.loaderHeaders(3000),
          observe: 'body',
          responseType: 'json'
        }
      )
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (error.status !== 400 || !error.error || !CashSettlementDraftErrorCode.includes(error.error.code)) {
      console.error(error);
    }

    return throwError(() => error.error || 'Server error');
  }

  public history(cashSettlementNo: any): Observable<any> {
    const storeNo = this.retrieveStore().storeNo;
    const url = this.getFullUrl(this.envService.history, {
      storeNo,
      cashSettlementNo
    });

    return this.http
      .get<any>(url, {
        headers: this.loaderHeaders(null),
        observe: 'body'
      })
      .pipe(
        map((res) => {
          return res;
        })
      );
  }

  public masterBank(): Observable<any> {
    const storeNo = this.retrieveStore().storeNo;
    const url = this.getFullUrl(this.envService.masterBank, {
      storeNo
    });

    return this.http
      .get<any>(url, {
        headers: this.loaderHeaders(null),
        observe: 'body'
      })
      .pipe(
        map((res) => {
          return res;
        })
      );
  }

  public print(cashSettlementNo: any): Observable<any> {
    const storeNo = this.retrieveStore().storeNo;
    const url = this.getFullUrl(this.envService.print, {
      storeNo,
      cashSettlementNo
    });

    let headers = this.loaderHeaders(300);
    headers = headers.set('Content-Type', 'application/pdf');

    return this.http
      .get<any>(url, {
        headers: headers,
        observe: 'body'
      })
      .pipe(
        map((res) => {
          return res;
        })
      );
  }

  public submitTransferEvidence(request: any): Observable<any> {
    const storeNo = this.retrieveStore().storeNo;
    const url = this.getFullUrl(this.envService.submitTransferEvidence, {
      storeNo
    });

    const formData = new FormData();

    for (const key in request) {
      if (request.hasOwnProperty(key)) {
        if (key === 'transferSlip' && !request.isAttachNewEvidence) {
          continue;
        }
        formData.append(key, request[key]);
      }
    }

    return this.http
      .post<any>(url, formData, {
        headers: this.loaderHeaders(100),
        observe: 'body',
        responseType: 'json'
      })
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }
}
